import React, { useEffect, useState } from 'react';
import Modal from "react-modal";

import './ModalPatientExtend.scss';
import Select from "../forms/inputs/Select";
import TextArea from "../forms/inputs/TextArea";
import { useMutation } from '@apollo/client';
import gqlSchemaLibrary from '../services/graphql.schemas';
import {getDateFormat, getDOB} from "../utils/date";
import {useTranslation} from "react-i18next";
import {ModalLog} from "../services/interactions/elements/modal";
import AuditTrail, {PAGES} from "../services/interactions/audit.trail";

const customStyles = {
    content: {
        top: '50%',
        left: '50%',
        width: '670px',
        right: 'auto',
        bottom: 'auto',
        marginRight: '-50%',
        transform: 'translate(-50%, -50%)',
        border: "none",
        padding: "0px",
        overflow: 'visible',
        boxShadow: "0 3px 8px 0 rgba(0, 0, 0, 0.38)",
    }
};

Modal.setAppElement('#root');

const _Modal = ModalLog(Modal);

const ModalPatientExtent = ({ currentOrg, state, patient, isOpen, onStatusChange }) => {
    const {t} = useTranslation(['participants','common']);
    const [openModal, setOpenModal] = useState(false);
    const [selectedTime, setSelectedTime] = useState(-1);
    const [reason, setReason] = useState(0);
    const [note, setNote] = useState('');
    const [daysCustomTime, setDaysCustomTime] = useState(1);
    const [onAction, setOnAction] = useState(false);
    // MUTATION METHODS
    const [pauseEnrollment] = useMutation(gqlSchemaLibrary.patients.mutations.pauseEnrollment, {
        onCompleted: ({ pauseEnrollment }) => {
            console.log('pauseEnrollment: ', pauseEnrollment);
            setOnAction(false);
            _onClose(pauseEnrollment);
        },
        onError: (error) => {
            setOnAction(false);
        }
    });

    const [extendEnrollment] = useMutation(gqlSchemaLibrary.patients.mutations.extendEnrollment, {
        onCompleted: ({ extendEnrollment }) => {
            console.log('extendEnrollment: ', extendEnrollment);
            setOnAction(false);
            _onClose(extendEnrollment);
        },
        onError: (error) => {
            setOnAction(false);
        }
    });
    const [disenrollPatient] = useMutation(gqlSchemaLibrary.patients.mutations.disenrollPatient, {
        onCompleted: ({ disenrollPatient }) => {
            console.log('disenrollPatient: ', disenrollPatient);
            setOnAction(false);
            _onClose(disenrollPatient);
        },
        onError: (error) => {
            setOnAction(false);
        }
    });

    const extendDays = [
        {
            label: t('extend-days.3-days'),
            value: 3
        },
        {
            label: t('extend-days.1-week'),
            value: 7
        },
        {
            label: t('extend-days.2-weeks'),
            value: 14
        }
    ];

    const reasonsExtend = [
        {
            label: t('reasons-extend.clinical-decision'),
            value: 1
        },
        {
            label: t('reasons-extend.participants-request'),
            value: 2
        },
        {
            label: t('reasons-extend.other'),
            value: 3
        }
    ];
    const reasonsDisenroll = [
        {
            label: t('reasons-disenroll.participant-is-well'),
            value: 2,
            byParticipant: false,
        },
        {
            label: t('reasons-disenroll.issues-with-hardware'),
            value: 3,
            byParticipant: false,
        },
        {
            label: t('reasons-disenroll.participant-not-happy'),
            value: 4,
            byParticipant: false,
        },
        {
            label: t('reasons-disenroll.provider-discontinued-completed'),
            value: 1,
            byParticipant: false,
        },
        {
            label: t('reasons-disenroll.provider-discontinued-higher-level'),
            value: 5,
            byParticipant: false,
        },
        {
            label: t('reasons-disenroll.provider-discontinued-other'),
            value: 6,
            byParticipant: false,
        },
        {
            label: t('reasons-disenroll.participant-died'),
            value: 7,
            byParticipant: false,
        },
        {
            label: t('reasons-extend.other'),
            value: 8,
            byParticipant: false,
        }
    ];
    const reasonsPause = [
        {
            label: t('reasons-pause.participant-was-hospitalized'),
            value: 1
        },
        {
            label: t('reasons-pause.participant-time-off'),
            value: 2
        },
        {
            label: t('reasons-disenroll.issues-with-hardware'),
            value: 3
        },
        {
            label: t('reasons-disenroll.participant-not-happy'),
            value: 4
        },
        {
            label: t('reasons-extend.other'),
            value: 5
        }
    ];

    useEffect(() => {
        setOpenModal(isOpen);
    }, [isOpen, state]);

    const getModalTitle = () => {
        switch (state) {
            case 1:
                // Enrolled state
                return t('extend-enrollment');
            case 2:
                // Pause state
                return t('pause-enrollment');
            case 3:
                // Disenrolled state
                return t('disenroll-participant');
            default:
                return '';

        }
    }
    const getOptionsReasons = () => {
        switch (state) {
            case 1:
                // Enrolled state
                return reasonsExtend;
            case 2:
                // Pause state
                return reasonsPause;
            case 3:
                // Disenrolled state
                return reasonsDisenroll;
            default:
                return [];

        }
    }
    const getSelectedReasonPlaceholder = () => {
        switch (state) {
            case 1:
                // Enrolled state
                return t('reasons-extend.title');
            case 2:
                // Pause state
                return t('reasons-pause.title');
            case 3:
                // Disenrolled state
                return t('reasons-disenroll.title');
            default:
                return '';

        }
    }
    const getTextButton = () => {
        switch (state) {
            case 1:
                // Enrolled state
                return t('common:save');
            case 2:
                // Pause state
                return t('pause-enrollment-no-capitalize');
            case 3:
                // Disenrolled state
                return t('disenroll');
            default:
                return '';

        }
    }

    const getDaysToSet = () => {
        if (selectedTime === 0) {
            return daysCustomTime;
        }
        return selectedTime;
    }
    const _onStatusChange = () => {
        if (onAction) {
            return;
        }
        setOnAction(true);
        const _reasonOptions = getOptionsReasons();
        const _reason = _reasonOptions.find(r => r.value === reason).label;
        if (state === 1) {
            // todo: extend program for patient
            //Enrolled state
            if (selectedTime === 0) {
                // Custom Time
                console.log('Add Days: ', daysCustomTime, selectedTime);
                AuditTrail.sendLog(PAGES.PARTICIPANTS_LIST, {tab: 'button', event: 'onClick', action: 'button save patient extend', days: daysCustomTime, patientId: patient._id, reason: _reason, note: note});
                extendEnrollment({
                    variables: {
                        personId: patient._id,
                        days: daysCustomTime,
                        reason: _reason,
                        note: note
                    }
                })

            } else {
                console.log('Add Days: ', selectedTime, selectedTime);
                AuditTrail.sendLog(PAGES.PARTICIPANTS_LIST, {tab: 'button', event: 'onClick', action: 'button save patient extend', days: selectedTime, patientId: patient._id, reason: _reason, note: note});
                extendEnrollment({
                    variables: {
                        personId: patient._id,
                        days: selectedTime,
                        reason: _reason,
                        note: note
                    }
                })
            }
        } else if (state === 2) {
            // Pause state
            AuditTrail.sendLog(PAGES.PARTICIPANTS_LIST, {tab: 'button', event: 'onClick', action: 'button pause enrollment', patientId: patient._id, reason: _reason, note: note});
            pauseEnrollment({
                variables: {
                    personId: patient._id,
                    reason: _reason,
                    note
                }
            })
        } else if (state === 3) {
            // Disenrolled state
            AuditTrail.sendLog(PAGES.PARTICIPANTS_LIST, {tab: 'button', event: 'onClick', action: 'button dis enrollment', patientId: patient._id, reason: _reason, note: note});
            disenrollPatient({
                variables: {
                    personId: patient._id,
                    reason: _reason,
                    note
                }
            })
        } else {
            setOnAction(false);
        }
    }
    const _onClose = (updatedPatient = null) => {
        setSelectedTime(-1);
        setReason(0);
        setNote('');
        setDaysCustomTime(1);
        onStatusChange(updatedPatient);
    }
    const closeModal = () => {
        _onClose(null);
    }

    const onSelectedTime = (value) => {
        return () => {
            AuditTrail.sendLog(PAGES.PARTICIPANTS_LIST, {tab: 'button', event: 'onClick', action: 'button modal change time patient extend', value: value, patientId: patient._id});
            setSelectedTime(value);
        }
    }
    const onReasonChange = (v) => {
        AuditTrail.sendLog(PAGES.PARTICIPANTS_LIST, {tab: 'button', event: 'onClick', action: 'button modal change reason patient extend', value: v, patientId: patient._id});
        setReason(v);
    }
    const onChangeNote = (v) => {
        setNote(v)
    }
    const onChangeCustomTime = (e) => {
        let value = e.target.value;
        if (!value) {
            value = '1';
        }
        AuditTrail.sendLog(PAGES.PARTICIPANTS_LIST, {tab: 'button', event: 'onClick', action: 'button modal change time patient extend', value: parseInt(value), patientId: patient._id});
        setDaysCustomTime(parseInt(value));
    }

    const isDisabled = () => {
        if (reason === 0) {
            return true;
        }
        if (state === 1) {
            if (selectedTime === 0 && daysCustomTime < 1) {
                return true
            }
            return selectedTime === -1;
        }
        return false;
    }

    if (!patient) {
        return '';
    }

    const daysModified = selectedTime === 0 && !!daysCustomTime ? daysCustomTime : (selectedTime > 0  ? selectedTime : 0);

    const getDaysOrHoursForDisenrolled = () => {
        const hours = patient.expectedUnenrollment? Math.floor((patient.expectedUnenrollment - Date.now()) / (1000 * 60 * 60)) : 0;
        return hours + ' hour'+ (hours === 1? '' : 's');
    }

    const expectedUnenrollment = patient.expectedUnenrollment? getDateFormat(currentOrg, patient.expectedUnenrollment + (daysModified * 24 * 60 * 60 * 1000), 'long') : t('no-unenrollment-set');

    return (
        <_Modal
            isOpen={openModal}
            onRequestClose={closeModal}
            style={customStyles}
            label="PatientExtended"
            event={PAGES.PARTICIPANTS_LIST}
            eventParams={{patient_id: patient? patient._id : null}}>
            <div className="modalExtend">
                <div className="modalHeader">
                    <div className="_title">
                        {getModalTitle()}
                    </div>
                    <div className="iconClose">
                        <span className="material-icons" onClick={closeModal}>
                            close
                            </span>
                    </div>
                </div>
                <div className="modalBody">
                    <div className="_patient">
                        <div className="_patientContent">
                            <p className="_text">
                                {patient.user.bio.lastname}, {patient.user.bio.firstname}
                            </p>
                            <div className="_patRow">
                                {getDOB(currentOrg, patient.dateOfBirth, patient.user.bio.gender)}
                            </div>
                        </div>
                    </div>
                    {
                        state === 3 ?
                            <span className="legendDisenroll">
                                {t('reasons-disenroll.text-1')} <b>{getDaysOrHoursForDisenrolled()}</b> {t('reasons-disenroll.text-2')}
                            </span> : ''
                    }
                    <div className="_extendTime">
                        {
                            state === 1 ? <p>{t('reasons-extend.extend-time')} <span className="subs">(<span className={`${!!daysModified ? 'modified': ''}`}>{!!daysModified ? t('reasons-extend.new-expected-unenrollment') : t('reasons-extend.expected-unenrollment')}: </span>{expectedUnenrollment})</span></p> : ''
                        }
                        {
                            state === 1 ?
                                <div className="_items">
                                    {
                                        extendDays.map((item, index) => (
                                            <span className={`_time ${selectedTime === item.value ? '_selected' : ''}`} key={index} onClick={onSelectedTime(item.value)}>{item.label}</span>
                                        ))
                                    }
                                    <span className={`_custom ${selectedTime === 0 ? '_selected' : ''}`} onClick={onSelectedTime(0)}>
                                        {t('custom-time')}
                                        </span>
                                </div> : ''
                        }
                        {
                            state === 1 && selectedTime === 0 ?
                                <div className="_customTime">
                                    <span className="_label">Other</span>
                                    <input type="number" min={1} max={31} value={daysCustomTime} onChange={onChangeCustomTime} />
                                    <span className="_days">day(s)</span>
                                </div> : ''
                        }
                        <Select required={false} value={reason} onChange={onReasonChange} placeholder={getSelectedReasonPlaceholder()} options={getOptionsReasons()} />
                        <TextArea placeholder={t('add-note')} value={note} onChange={onChangeNote} _label={'patient extended note'} event={PAGES.PARTICIPANTS_LIST} eventParams={{patient_id: patient? patient._id : null}} />
                    </div>
                </div>
                <div className="modalFooter">
                    {
                        selectedTime !== -1 ? (
                        <div className="legend">
                        </div>) : <div></div>
                    }
                    <button className="button link" onClick={closeModal}>{t('common:cancel')}</button>
                    <button className={`button primary state${state} ${isDisabled() ? 'disabled' : ''}`} disabled={isDisabled()} onClick={_onStatusChange}>
                        {getTextButton()}
                    </button>
                </div>
            </div>
        </_Modal>
    )
}

export default ModalPatientExtent;
